<template>
  <div class="flex flex-column gap-3" data-testid="time-off-view-info">
    <a-descriptions :column="1" size="small" class="col-fixed p-0" data-testid="time-off-view-info-descriptions">
      <a-descriptions-item label="Associate">
        <grouped-associate-info
          :first-name="timeOff.associate.first_name"
          :last-name="timeOff.associate.last_name"
          :photo-url="timeOff.associate.photo_url"
          :unverified="!timeOff.associate.is_verified"
          :role="timeOff.associate.role.name"
          :associate-number="timeOff.associate.associate_number"
          :payment-info="{
            payment_type: timeOff.associate.payment_info.type,
            method: timeOff.associate.payment_info.method,
            legal_entity: timeOff.associate.payment_info.legal_entity
          }"
        />
      </a-descriptions-item>
      <a-descriptions-item label="Submitted At">
        {{ formatDatetimeString(timeOff.created_at) }}
      </a-descriptions-item>
      <a-descriptions-item label="Status">
        <a-tag class="m-0" :color="getTimeOffStatus(timeOff.status)?.color">{{
          getTimeOffStatus(timeOff.status)?.title ?? ' - '
        }}</a-tag>
      </a-descriptions-item>
      <a-descriptions-item label="Reason">
        <a-tag class="m-0" :color="getTimeOffType(timeOff.type)?.color">{{
          getTimeOffType(timeOff.type)?.title ?? ' - '
        }}</a-tag>
      </a-descriptions-item>
      <a-descriptions-item label="Pay Preference">
        <a-tag class="m-0" :color="timeOff.need_payments ? '#10b981' : '#6b7280'">
          {{ timeOff.need_payments ? 'Yes' : 'No' }}
        </a-tag>
      </a-descriptions-item>
      <a-descriptions-item label="Days">
        {{ getAllDays }}
      </a-descriptions-item>
      <a-descriptions-item v-if="timeOff.type === 4" label="Explanation">
        {{ timeOff.comment ?? '-' }}
      </a-descriptions-item>
      <a-descriptions-item label="Files">
        <time-off-attachments :files="timeOff.files || []" :with-title="false" @save="saveAttachmentsIds" />
      </a-descriptions-item>
      <a-descriptions-item v-if="timeOff.author" label="Created By">
        <grouped-associate-info
          v-if="!isSystemAuthor(timeOff.author)"
          :first-name="timeOff.author.first_name"
          :last-name="timeOff.author.last_name"
          :photo-url="timeOff.author.photo_url"
          :associate-number="timeOff.author.associate_number"
        />
        <span v-else>System</span>
      </a-descriptions-item>
      <a-descriptions-item v-if="timeOff.review_days" label="Day Off Reviewed By">
        <grouped-associate-info
          v-if="!isSystemAuthor(timeOff.review_days.author)"
          :first-name="timeOff.review_days.author.first_name"
          :last-name="timeOff.review_days.author.last_name"
          :photo-url="timeOff.review_days.author.photo_url"
          :associate-number="timeOff.review_days.author.associate_number"
        />
        <span v-else>System</span>
      </a-descriptions-item>
      <a-descriptions-item v-if="timeOff.review_payments" label="Payment Reviewed By">
        <grouped-associate-info
          v-if="!isSystemAuthor(timeOff.review_payments.author)"
          :first-name="timeOff.review_payments.author.first_name"
          :last-name="timeOff.review_payments.author.last_name"
          :photo-url="timeOff.review_payments.author.photo_url"
          :associate-number="timeOff.review_payments.author.associate_number"
        />
        <span v-else>System</span>
      </a-descriptions-item>
    </a-descriptions>

    <time-off-statistic v-if="timeOff.associate.payment_info.type !== 'Contractor'" data-testid="time-off-view-statistic" />

    <template v-if="[3, 4].includes(timeOff.status)">
      <h4 class="m-0">Dates</h4>
      <a-table
        data-testid="time-off-view-days-table"
        :rowKey="record => record.id"
        size="small"
        :columns="
          timeOff.associate.payment_info.type === 'Contractor'
            ? columns.filter(item => item.key !== 'payment')
            : columns
        "
        :data-source="timeOff.items"
        :scroll="{ y: 200 }"
      >
        <span slot="status" slot-scope="text, record"
          ><a-tag class="m-0" :color="record.shift ? '#10b981' : '#ef4444'">{{
            record.shift ? 'Approved' : 'Declined'
          }}</a-tag>
        </span>
        <span slot="payment" slot-scope="text, record"
          ><a-tag
            v-if="record.shift"
            :color="getPayCodeReadyForPayStatus(record.shift.internal_payment_info.ready_for_pay_status).color"
            >{{ getPayCodeReadyForPayStatus(record.shift.internal_payment_info.ready_for_pay_status).title }}</a-tag
          >
          <span v-else>-</span>
        </span>
      </a-table></template
    >
  </div>
</template>

<script>
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { Descriptions, notification, Table, Tag } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import TimeOffViewStatistic from './time-off-view-statistic.vue'
import util from '@/util'

const { mapActions, mapState, mapGetters } = createNamespacedHelpers('timeOff')
const { mapGetters: shiftGetters } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-tag': Tag,
    'a-table': Table,
    'time-off-attachments': ShiftFormAttachments,
    'time-off-statistic': TimeOffViewStatistic,
    'grouped-associate-info': GroupedAssociateInfo
  },
  data() {
    return {
      columns: [
        {
          title: 'Date',
          key: 'date',
          width: '100px',
          customRender: (_, record) => {
            const { date } = record

            return this.formatDateString(date)
          }
        },
        {
          title: 'Status',
          key: 'status',
          width: '100px',
          scopedSlots: {
            customRender: 'status'
          }
        },
        {
          title: 'Payment',
          key: 'payment',
          scopedSlots: {
            customRender: 'payment'
          }
        }
      ]
    }
  },
  computed: {
    ...mapState({
      timeOff: state => state.timeOff
    }),
    ...shiftGetters(['getPayCodeReadyForPayStatus']),
    ...mapGetters(['getTimeOffStatus', 'getTimeOffType', 'getTimeOffRequestType']),

    timeZone() {
      return this.$store.state.applicationState.timeZone
    },
    getAllDays() {
      return this.timeOff.items
        .map(item => {
          const date = moment.utc(item.date).tz(this.timeZone)
          const currentYear = moment().year()

          return date.format(date.year() === currentYear ? 'MMM DD' : 'MMM DD YYYY')
        })
        .join('; ')
    }
  },
  methods: {
    ...mapActions(['updateTimeOffAttachments']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async saveAttachmentsIds(ids) {
      try {
        await this.updateTimeOffAttachments({
          id: this.timeOff.id,
          files: ids.map(item => item.id)
        })

        this.showNotification('success', 'Success', 'Attachments were successfully changed')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    formatDateString(date) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(date, 'MM/DD/YYYY')
    },
    formatDatetimeString(date) {
      return util.convertDateTimeToCurrentTimeZoneFormattedString(date)
    },
    isSystemAuthor(author) {
      return author.is_system
    }
  }
}
</script>
